import { ReactNode, useEffect, useRef } from "react";

/**
 * A dialog for forms that would otherwise sit permanently in the page.
 *
 * The pages that needed this had grown a habit of toggling a full form inline -- "Invite a client",
 * "Add to library" -- which pushed the actual content (the roster, the exercises) down the page and left
 * a large empty form occupying the screen whenever it was open. A modal puts the form in front when it
 * is wanted and returns the page to its content when it is not.
 *
 * Deliberately not a <dialog>: showModal() is imperative and would need a ref dance to stay in step with
 * React state, and its default backdrop is awkward to theme consistently with the rest of the app.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * The latest onClose, without it being a dependency of anything.
   *
   * Every caller passes an inline arrow (`onClose={() => setPicker(null)}`), so its identity changes on
   * every render. This used to be a dependency of the effect below, which meant: type a character ->
   * state change -> re-render -> new onClose -> effect re-runs -> focus() yanked the cursor out of the
   * input and back onto the dialog. Typing in a modal was effectively impossible, on every modal in the
   * app. The ref keeps Escape wired to the current handler while the effect itself runs once.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    /**
     * Escape closes it. Without this the only way out is the button, which is a trap for anyone who
     * expects the usual behaviour -- and on a form that is mostly optional fields, "I changed my mind"
     * is the common case rather than the rare one.
     */
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKeyDown);

    // Focus moves into the dialog so keyboard and screen-reader users are not left behind on the page
    // underneath, still tabbing through content that is now visually obscured. Once, on open.
    panelRef.current?.focus();

    // The page behind must not scroll while a modal is open: on a phone especially, scrolling the
    // backdrop instead of the form is disorienting and makes the form feel broken.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // Deliberately empty: this must run exactly once per open. See onCloseRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="chip" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
